/**
 * @class Tracking
 * @module Ladies
 *
 * Die Tracking-Klasse bietet Funktionialitäten zum Erstellen von
 * 	Tracking-Pixeln auf der Seite, deren Bild-URL ein Skript zum Zählen
 * 	von Zugriffszahlen aufruft.
 *
 * Durch die Präsenz eines Timestamps in der URL werden diese niemals
 * 	gecacht, was gewährleistet, dass wirklich jeder Aufruf gezählt
 * 	werden kann. Des Weiteren verbleibt immer nur ein Pixel im Quelltext
 * 	der Seite, da neue Pixel die alten Tags ersetzen.
 *
 * @param {jQuery|Function} $
 *
 * @member {Object} siteConfig
 * 	@property {String} trackingPath
 * 	@property {String} product
 * 	@property {String} trackingParameters
 */

import { hasOwn } from "@modules/util/has-own"

export const trackBannerClick = (trackingClickUrl: string) => {
    const jetzt = new Date()

    const url = trackingClickUrl.replace("_time_", jetzt.getTime().toString())

    const trImg = document.createElement("img")
    trImg.setAttribute("src", url)
    trImg.setAttribute("width", "1")
    trImg.setAttribute("height", "1")

    window.document.body.appendChild(trImg)
}

export class TrackingClass {
    /**
     * @constructor
     */
    constructor() {
        // Automatisches Tracken für Links mit bestimmtem data-*-Attribut.
        // 	Die TrackingId besteht aus kommagetrennter AuftragsID und Place.
        // 	Beispiel:  <a href="#" data-tracking-id="9432545,234342">
        $(document).on("click", "a[data-tracking-id]", this.track.bind(this))
    }

    /**
     * Zeitstempel zum aushebeln des Caches
     *
     * @private
     * @type {Number}
     */
    trackingTime = 0

    /**
     * Der Name des aktuell geladenen Produktes (z.Bsp. ladies)
     *
     * @private
     * @type {String}
     */
    product = ""

    /**
     * Ein Objekt mit den Keys und Werten, welche beim Erstellen des
     * 	Pixels in URL-Query-Parameter umgewandelt werden
     *
     * @private
     * @type {Object}
     */
    parameters = {}

    /**
     * Aufrufen, um externe URL-Aufrufe zu tracken
     *
     * @public
     */
    trackRedirect() {
        this.generateTrackingPixel(siteConfig.trackingTagsMap.bannerlink)
    }

    /**
     * Aufrufen, um externe URL-Aufrufe zu tracken
     *
     * @public
     */
    trackEinzelansicht() {
        this.generateTrackingPixel(siteConfig.trackingTagsMap.einzelansicht)
    }

    /**
     * Aufrufen, um externe URL-Aufrufe zu tracken
     *
     * @public
     */
    trackNews() {
        this.generateTrackingPixel(siteConfig.trackingTagsMap.news)
    }

    /**
     * Aufrufen, um externe URL-Aufrufe zu tracken
     *
     * @public
     */
    trackAnzeigenlink() {
        this.generateTrackingPixel(siteConfig.trackingTagsMap.anzeigenlink)
    }

    /**
     * Tracke über BezugsID verknüpfte Anzeigen
     *
     * @public
     */
    trackBezugslink(auftragsid: string, place: string) {
        this.generateTrackingPixel(siteConfig.trackingTagsMap.einzelansicht, {
            auftragsid: auftragsid,
            place: place,
        })
    }

    /**
     * Aufrufen, um Galerie-Klicks zu tracken
     *
     * @public
     */
    trackGallery(newTag: string | null = null) {
        let tag = newTag
        if (!tag) {
            tag = siteConfig.trackingTagsMap.bildansicht
        }
        this.generateTrackingPixel(tag)
    }

    /**
     * Aufrufen, um externe URL-Aufrufe zu tracken
     *
     * @public
     * @param {number} auftragsid
     * @param {number} place
     * @param {boolean} loadImmediately
     */
    trackBannerEinblendung(auftragsid: string | number, place: string | number, loadImmediately = true) {
        this.generateTrackingPixel(
            siteConfig.trackingTagsMap.einblendung,
            {
                auftragsid: auftragsid,
                place: place,
            },
            false,
            loadImmediately,
        )
    }

    /**
     * Aufrufen, um externe URL-Aufrufe zu tracken
     *
     * @public
     */
    trackBannerlink(auftragsid: string, place: string) {
        this.generateTrackingPixel(siteConfig.trackingTagsMap.bannerlink, {
            auftragsid: auftragsid,
            place: place,
        })
    }

    /**
     * Aufrufen, um externe URL-Aufrufe zu tracken
     *
     * @public
     */
    trackNewslink(auftragsid: string, place: string) {
        this.generateTrackingPixel(siteConfig.trackingTagsMap.einzelansicht, {
            auftragsid: auftragsid,
            place: place,
        })
    }

    /**
     * Aufrufen, um Telefonsex zu tracken
     *
     * @public
     * @param {Number} [auftragsid]
     */
    trackTelefonsex(auftragsid: string) {
        this.generateTrackingPixel(siteConfig.trackingTagsMap.einzelansicht, {
            auftragsid: auftragsid,
            publikation: "Telefonsex",
        })
    }

    /**
     * Generiert automatisch ein Tracking-Pixel für den geklickten Link
     *
     * @private
     * @param {Event} clickEvent
     *
     * @see trackBezugslink
     */
    track(clickEvent: MouseEvent) {
        const trackingLinkElement = $(clickEvent.currentTarget)
        const trackingId = trackingLinkElement.data("trackingId").split(",")
        const trackingType = trackingLinkElement.data("trackingType") || "bannerlink"
        // Notwendige Haupt-Attribute
        const auftragsid = trackingId[0]
        const place = trackingId[1]

        // Generiere Methodenaufruf aus dem Tracking-Typ
        const trackingCall = "track".concat(
            trackingType.replace(/^(\w)/, (firstLetter: string) => firstLetter.toUpperCase()),
        )

        // Rausspringen, wenn der Tracking-Typ nicht existiert
        if (!(trackingCall in this)) {
            return
        }

        // Da es sich hier um einen dynamischen Aufruf handelt, bestehende
        // 	Config zwischenspeichern, da ein Link nicht unbedingt die
        // 	aktuelle Seite wechseln muss und ansonsten bei weiteren
        // 	Tracking-Aufrufen die Werte verfälscht sein könnten
        const keepTrackingParameters = siteConfig.trackingParameters

        try {
            // Mit try-catch, damit auch bei Fehlschlag die alte
            // 	Config wiederhergestellt werden kann
            this.parseTrackingParameters(trackingLinkElement)
            // @ts-expect-error old cold and before is a check if tracking call in this
            this[trackingCall](auftragsid, place)
        } catch (e) {}

        // Alte Config wiederherstellen
        siteConfig.trackingParameters = keepTrackingParameters
    }

    /**
     * Ersetzt die Config-Properties mit den Angaben der data-*-Attribute
     * 	des aktuell angeklickten Links. Diese werden später zurückgesetzt.
     *
     * @private
     * @param {jQuery|Object} trackElement
     */
    parseTrackingParameters(trackElement: typeof $) {
        const data = trackElement.data()
        const allowedKeys = ["domain", "name", "publikation", "rubrik"]

        siteConfig.trackingParameters = siteConfig.trackingParameters || {}

        for (const key in data) {
            if (!hasOwn(data, key) || allowedKeys.indexOf(key) < 0) {
                continue
            }

            // @ts-expect-error old cold, check beforehand if key exists
            siteConfig.trackingParameters[key] = data[key]
        }
    }

    /**
     * Generiert das Tracking-Pixel und setzt dieses auf der Seite
     *
     * @private
     * @param {String} type
     * @param {Object} [extraParameters]
     * @param {Boolean} replaceOldTag
     * @param {Boolean} loadImmediately
     */
    generateTrackingPixel(
        type: string,
        extraParameters: object = {},
        replaceOldTag = true,
        loadImmediately = true,
    ): void {
        const oldTracker = replaceOldTag ? $(".tracker") : []

        this.prepareUrlValues()

        // Falls optionale weitere Parameter übergeben wurden, diese hier
        // 	dem Parameter-Objekt hinzufügen
        $.extend(this.parameters, extraParameters)

        if (!this.parametersValidated(this.parameters)) {
            console.error(
                "Tracking-Parameter konnten nicht validiert werden. Tracking-Pixel wird nicht erstellt.",
                this.parameters,
            )
            return
        }

        // Pixel wird erstellt
        const trackerPixel = $("<img />")
            .prop("width", 1)
            .prop("height", 1)
            .css("display", "none")
            .addClass("tracker")
            .addClass("t-a".concat(Array.from(Object.values(extraParameters)).join("-p")))

        if (loadImmediately) {
            trackerPixel.prop("src", this.generateTrackingUrl(type))
        } else {
            trackerPixel.attr("data-src", this.generateTrackingUrl(type))
        }

        // Pixel wird in den Body eingefügt oder ersetzt das alte Pixel
        if (!oldTracker.length) {
            $("body").append(trackerPixel)
        } else {
            oldTracker.replaceWith(trackerPixel)
        }
    }

    isNumeric(value: any): boolean {
        return !Number.isNaN(value) && !Number.isNaN(Number.parseFloat(value))
    }

    parametersValidated(parameters: any): boolean {
        const requiredParameters = ["place", "auftragsid", "domain"]

        for (const element of requiredParameters) {
            if (!parameters[element]) {
                return false
            }
        }

        if (!this.isNumeric(parameters.place)) {
            return false
        }

        if (!this.isNumeric(parameters.auftragsid)) {
            return false
        }

        return parameters.domain !== ""
    }

    /**
     * Setzt bestimmte, von der URL benötigte Werte auf Instanzvariablen
     *
     * @private
     */
    prepareUrlValues() {
        this.trackingTime = new Date().getTime()
        this.product = siteConfig.trackingName
        this.parameters = siteConfig.trackingParameters || {}
    }

    /**
     * Generiert die URL, die dem neuen Pixel als Source zugewiesen wird
     *
     * @private
     * @param {String} type
     * @return {String}
     */
    generateTrackingUrl(type: string) {
        const urlBase = `${siteConfig.trackingPath + this.product}_${type}`
        const urlParameters = $.extend(this.parameters, { tmp: this.trackingTime })

        this.cleanParameters(urlParameters)

        // Umwandlung des Objektes in einen URL-Query-String via jQuery
        return `${urlBase}?${$.param(urlParameters)}`
    }

    /**
     * Entfernt alle nicht gesetzten Properties aus dem Parameter-Objekt
     *
     * @private
     * @param {Object} parameters
     */
    cleanParameters(parameters: Record<string, string>) {
        for (const key of Object.keys(parameters)) {
            // Nur in diesen Fällen das Property entfernen
            if (undefined === parameters[key] || null === parameters[key]) {
                delete parameters[key]
            }

            if (
                ["{__REPLACE_TRACKING_ADVERT_NAME__}", "{__REPLACE_TRACKING_CATEGORY__}"].indexOf(parameters[key]) >= 0
            ) {
                delete parameters[key]
            }

            // Leerstring, Integer 0 und Boolean false werden beibehalten
        }
    }
}

window.trackBannerClick = trackBannerClick
window.Tracking = new TrackingClass()
